import React from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import logoUrl from 'assets/logo_manty_new.png';
import PrintableFilterBar from 'routes/Dashboard/components/WidgetsLayout/PrintableGrid/PrintableFilterBar/PrintableFilterBar';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { GRID_NB_COLS, ROW_HEIGHT, VERTICAL_MARGIN } from './constants';
import { getYOffset, PrintableWidget } from './helpers/paginationHelpers';
import Flex from 'ui/Flex/Flex';

/** TODO
 * This page is quite a shame of a code, it should be refactored. Unfortunately, we'd prefer
 * not to break the existing height of the widgets container, so it is a constraint that must
 * be respected. A suggested refactor would be to set an arbitrary height for the widgets container
 * and then use CSS Flex or Grid to have a nice and fluid layout for the header components, instead of this
 * mix of pixels, cm, rems, flex, absolute positioning, etc.
 */

const StyWidgetContainer = styled(Box, { name: 'StyWidgetContainer' })({
    position: 'relative',
    maxWidth: 'none',
});

const StyHeaderContainer = styled(Box, {
    name: 'StyHeaderContainer',
    shouldForwardProp: (prop) => prop !== 'isFirstPage',
})<{ isFirstPage: boolean }>(({ isFirstPage }) => {
    const ZOOM_LEVEL = 0.6; // The zoom level used in .printable-page
    // these values do not match what you'll see with getBoundingClientRect(), because
    // between the /pdf web page and the generated PDF, there is a slight difference, and these
    // values have been chosen to match as close as possible the height in the PDF. There might be
    // a better way to derive these values.
    const MAGIC_FIRST_PAGE_WIDGETS_HEIGHT = 188.5;
    const MAGIC_OTHER_PAGES_WIDGETS_HEIGHT = 147.2;
    return {
        position: 'relative',
        background: 'white',
        margin: 0,
        /* the first page has a larger header because it contains the title and the date.
         * The two values are arbitrary to match exactly the existing height of the widgets container,
         * so any change on the header layout would not require every client to update their dashboard layout.
         */
        height: isFirstPage
            ? MAGIC_FIRST_PAGE_WIDGETS_HEIGHT / ZOOM_LEVEL
            : MAGIC_OTHER_PAGES_WIDGETS_HEIGHT / ZOOM_LEVEL,
        overflow: 'hidden',
    };
});

const StyLogo = styled('img', {
    name: 'StyLogo',
})({
    height: '1.5cm',
    margin: '0cm 0.5cm 0.5cm 0.5cm',
});

const StyLogosContainer = styled(Flex, {
    name: 'StyLogosContainer',
    shouldForwardProp: (prop) => prop !== 'isFirstPage',
})<{ isFirstPage: boolean }>(({ isFirstPage }) => ({
    height: isFirstPage ? '2cm' : '2.8cm',
    justifyContent: 'space-between',
    background: 'white',
    marginTop: '0.5cm',
    position: 'relative',
}));

const StyTitle = styled(Typography, {
    name: 'StyTitle',
})(({ theme }) => ({
    color: theme.palette.text.primary,
    fontSize: '2rem',
    lineHeight: '1.25',
    textAlign: 'center',
    width: '100%',
    // ellipsis related rules
    display: '-webkit-box',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
}));

type Props = {
    title: string;
    pageIndex: number;
    pageWidgets: PrintableWidget[];
    WidgetRender: (arg1: { widget: PrintableWidget }) => React.ReactElement;
    clientLogoUrl: string | null | undefined;
    filters: DashboardFilterT[];
    isDashboard?: boolean;
};

/*
 * Render one page of the printed PDF.
 * It uses the y RGL coordinates of the children (widgets) and absolute positioning.
 */
const PrintablePage = ({
    title,
    pageIndex,
    pageWidgets,
    WidgetRender,
    clientLogoUrl,
    filters,
    isDashboard = true,
}: Props): React.ReactElement => {
    /*
     * This is the function which determines how the widgets will be positioned, using
     * absolute positioning.
     *
     * TODO: separate computation from style.
     */

    const { t } = useTranslation();
    const childrenStyle = (widget: PrintableWidget): React.CSSProperties => {
        const widgetLayout = widget.position.md;
        const height =
            (ROW_HEIGHT + VERTICAL_MARGIN) * widgetLayout.h - VERTICAL_MARGIN;
        const originalHeight =
            (ROW_HEIGHT + VERTICAL_MARGIN) *
                (widget.originalHeight ?? widgetLayout.h) -
            VERTICAL_MARGIN;
        // Each page starts at 0, so we need to offset y with the size of the previous pages.
        const yOffset = getYOffset(pageIndex);
        if (widgetLayout.x === undefined || widgetLayout.y === undefined) {
            throw new Error(
                `PrintablePage error - undefined position for widget {key}`,
            );
        }
        const baseStyle: React.CSSProperties = {
            position: 'absolute',
            marginTop: `${VERTICAL_MARGIN}px`,
            height,
            left: `calc(100% * ${widgetLayout.x / GRID_NB_COLS})`,
            top: (widgetLayout.y - yOffset) * (ROW_HEIGHT + VERTICAL_MARGIN),
            width: `calc(100% * ${widgetLayout.w / GRID_NB_COLS})`,
        };
        const duplicatedStyle = {
            ...baseStyle,
            // for duplicated widget we adapt the top position with a overflow hidden
            // so that only the part that is truncated from the original widget is showed
            // for the top position, we set it at the widget height - original widget height
            // to let only the original widget truncated part visible
            zIndex: -1,
            top: height - originalHeight,
            height: originalHeight,
        };
        return widget.isTruncatedDuplicate ? duplicatedStyle : baseStyle;
    };
    const isFirstPage = pageIndex === 0;
    return (
        <Box className="printable-page">
            <StyHeaderContainer isFirstPage={isFirstPage}>
                <StyLogosContainer isFirstPage={isFirstPage}>
                    <StyLogo
                        role="presentation"
                        src={clientLogoUrl || logoUrl}
                    />
                    <StyLogo role="presentation" src={logoUrl} />
                </StyLogosContainer>
                {isFirstPage && (
                    <>
                        <StyTitle variant="body1Bold" paragraph>
                            {title}
                        </StyTitle>
                        <Typography
                            color="textPrimary"
                            variant="body1"
                            paragraph
                        >
                            {`${t(
                                'printPage.extractionDate',
                            )}: ${new Date().toLocaleDateString()}`}
                        </Typography>
                    </>
                )}
                {isDashboard && <PrintableFilterBar filters={filters} />}
            </StyHeaderContainer>
            <StyWidgetContainer>
                {pageWidgets.map((widget) => (
                    <Box
                        className="printable-grid-element"
                        sx={childrenStyle(widget)}
                        key={widget.key}
                    >
                        {WidgetRender({ widget })}
                    </Box>
                ))}
            </StyWidgetContainer>
        </Box>
    );
};

export default PrintablePage;
